import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { CheckCircle, Ruler, Palette, Heart } from "lucide-react";
import ContactBlock from "../../components/contact/ContactBlock";
import { Link } from "react-router-dom";

const ReturnPolicyScreen = () => {
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const guarantees = [
		{
			icon: <CheckCircle className="w-8 h-8 text-primary" />,
			title: "Guaranteed Fit",
			description:
				"We test all our shades before sending them to the customers. In the unlikely event that a shade does not match your measurements, we will replace the shade at no cost to you.",
		},
		{
			icon: <Ruler className="w-8 h-8 text-primary" />,
			title: "Measurement Flexibility and Support",
			description:
				"We understand that mistakes happen. If you find out that your measurements are inaccurate, contact us within fourteen (14) days from the date of purchase. We will work with you to find the best, most cost-effective solution. In certain situations, we will be able to adjust your shades to the proper measurements at no cost.",
		},
		{
			icon: <Palette className="w-8 h-8 text-primary" />,
			title: "Colour Accuracy",
			description:
				"Despite our best efforts to give you an accurate representation of the colours and textures of the fabrics listed on our website, the actual colour may differ due to technical differences in display devices. If you require an exact colour match, please request a sample of the fabrics you are interested in before placing an order. Also, we suggest buying all window shades you would like to have in a single colour in the same order. Dye lots slightly vary from time to time, so separate orders may not be an exact match.",
		},
		{
			icon: <Heart className="w-8 h-8 text-primary" />,
			title: "Satisfaction Guaranteed",
			description:
				"If you are not completely satisfied with the quality, colour, or design of your new shade, contact us within fourteen (14) days from the date of purchase. We will work with you to make it right.",
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-50">
			<Helmet>
				<title>Our Guarantee | Off Cut Shades</title>
				<meta
					name="description"
					content="Off Cut Shades guarentee. Our window shades come with a guaranteed fit, satisfaction guaranteed, colour accuracy, and measurement flexibility and support."
				/>
				<link
					rel="canonical"
					href="https://offcutshades.com/help/return-policy"
				/>
			</Helmet>

			<main className="container mx-auto px-4 py-12">
				{/* Hero Section */}
				<motion.div
					className="text-center mb-16"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
				>
					<motion.div
						className="flex justify-center mb-6"
						initial={{ scale: 0 }}
						animate={{ scale: 1 }}
						transition={{ ...bounceTransition, delay: 0.2 }}
					>
						<img
							src="/images/newHome/Return-policy-80x80.png"
							alt="Return Policy"
							className="w-20 h-20"
						/>
					</motion.div>

					<motion.h1
						className="relative z-10 text-5xl md:text-7xl font-bold mb-6 leading-tight"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.3 }}
					>
						Our{" "}
						<span className="relative">
							<span className="text-primary">Guarantee</span>
						</span>
					</motion.h1>
				</motion.div>

				{/* Guarantees Grid */}
				<div className="grid grid-cols-1 gap-8">
					{guarantees.map((guarantee, index) => (
						<motion.div
							key={index}
							className="bg-white rounded-3xl shadow-lg overflow-hidden"
							initial={{ opacity: 0, y: 50 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: index * 0.1 }}
							viewport={{ once: true }}
						>
							<div className="p-8">
								<div className="flex items-center gap-4 mb-4">
									{guarantee.icon}
									<h2 className="text-2xl font-bold text-gray-900">
										{guarantee.title}
									</h2>
								</div>
								<p className="text-gray-600">{guarantee.description}</p>
							</div>
						</motion.div>
					))}
				</div>
			</main>

			{/* Contact Support Section */}
			<ContactBlock />
		</div>
	);
};

export default ReturnPolicyScreen;
